import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { ZoomIn, ZoomOut, Check, X } from 'lucide-react';

const BOX = 280;      // taille d'aperçu à l'écran
const OUTPUT = 768;   // taille finale envoyée (qualité visuelle privilégiée)

/** Recadrage circulaire : glisser pour positionner le visage, curseur pour zoomer. */
export const AvatarCropper = ({ file, open, onCancel, onConfirm, busy = false }) => {
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      setImg(image);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    };
    image.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const base = img ? BOX / Math.min(img.width, img.height) : 1;
  const renderedW = img ? img.width * base * zoom : 0;
  const renderedH = img ? img.height * base * zoom : 0;

  const clamp = useCallback((pos) => {
    const maxX = Math.max(0, (renderedW - BOX) / 2);
    const maxY = Math.max(0, (renderedH - BOX) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, pos.x)),
      y: Math.max(-maxY, Math.min(maxY, pos.y)),
    };
  }, [renderedW, renderedH]);

  useEffect(() => {
    setOffset((o) => clamp(o));
  }, [zoom, clamp]);

  const onPointerDown = (e) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, origin: offset };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    setOffset(clamp({
      x: d.origin.x + (e.clientX - d.startX),
      y: d.origin.y + (e.clientY - d.startY),
    }));
  };

  const onPointerUp = () => { dragRef.current = null; };

  const handleConfirm = () => {
    if (!img) return;
    const ratio = OUTPUT / BOX;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT, OUTPUT);
    ctx.drawImage(
      img,
      (BOX / 2 + offset.x - renderedW / 2) * ratio,
      (BOX / 2 + offset.y - renderedH / 2) * ratio,
      renderedW * ratio,
      renderedH * ratio
    );
    canvas.toBlob((blob) => {
      if (!blob) return;
      const name = (file?.name || 'avatar').replace(/\.[^.]+$/, '') + '.jpg';
      onConfirm(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }));
    }, 'image/jpeg', 0.94);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
      <DialogContent className="sm:max-w-md" data-testid="avatar-cropper-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif">Cadre ton visage</DialogTitle>
          <DialogDescription>
            Fais glisser la photo et ajuste le zoom pour centrer ton visage dans le cercle.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-5 py-2">
          <div
            className="relative overflow-hidden rounded-full touch-none select-none cursor-grab active:cursor-grabbing"
            style={{ width: BOX, height: BOX, background: 'hsl(var(--paper))', boxShadow: '0 0 0 3px hsl(var(--gold)), 0 6px 18px rgba(40,28,18,.18)' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            data-testid="avatar-cropper-area"
          >
            {img && (
              <img
                src={img.src}
                alt="Recadrage"
                draggable={false}
                style={{
                  position: 'absolute',
                  left: BOX / 2 + offset.x - renderedW / 2,
                  top: BOX / 2 + offset.y - renderedH / 2,
                  width: renderedW,
                  height: renderedH,
                  pointerEvents: 'none',
                }}
              />
            )}
          </div>

          <div className="flex items-center gap-3 w-full px-4">
            <ZoomOut className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.01}
              onValueChange={([v]) => setZoom(v)}
              className="flex-1"
              data-testid="avatar-cropper-zoom"
            />
            <ZoomIn className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="avatar-cropper-cancel">
            <X className="w-4 h-4 mr-2" /> Annuler
          </Button>
          <Button onClick={handleConfirm} disabled={busy || !img} data-testid="avatar-cropper-confirm">
            {busy ? (
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Valider la photo
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AvatarCropper;
